/**
 * CanaryStep — third step of the onboarding wizard.
 *
 * Simplified Canary creation: page name + one authorized domain.
 * Hidden defaults: screenshotEnabled=false, confidenceThreshold=70.
 */

import { useRef, useState, type FormEvent, type RefObject } from 'react'
import { useMutation } from '@tanstack/react-query'
import { api, ApiError } from '@/services/api'
import { Button, Input } from '@/components/ui'
import { useAutoFocus } from '@/hooks/useAutoFocus'
import { normalizeDomain, validateDomain } from '@/utils/domain'

type CanaryStepProps = {
  organizationId: string
  onCreated: (canary: { id: string; token: string; name: string; domain: string; domainId: string }) => void
  headingRef?: RefObject<HTMLHeadingElement>
}

type FieldError = { field: 'name' | 'domain' | 'general'; message: string }

const DEFAULT_THRESHOLD = 70

function validateInputs(name: string, domain: string): FieldError | null {
  if (!name) return { field: 'name', message: 'Please give this page a name.' }
  if (name.length > 100)
    return { field: 'name', message: 'Page name must be 100 characters or fewer.' }
  const domainErr = validateDomain(domain)
  if (domainErr) return { field: 'domain', message: domainErr }
  return null
}

function mapCreateError(error: unknown): string {
  if (!(error instanceof ApiError)) return 'Something went wrong. Please try again.'
  return error.message || 'Unable to create canary. Please try again.'
}

export function CanaryStep({ organizationId, onCreated, headingRef }: CanaryStepProps) {
  const [name, setName] = useState('')
  const [domain, setDomain] = useState('')
  const [error, setError] = useState<FieldError | null>(null)
  const nameRef = useRef<HTMLInputElement>(null)
  useAutoFocus(nameRef)

  const mutation = useMutation({
    mutationFn: (payload: { name: string; domain: string }) =>
      api.createCanary(organizationId, {
        name: payload.name,
        authorizedDomains: [payload.domain],
        screenshotEnabled: false,
        confidenceThreshold: DEFAULT_THRESHOLD,
      }),
    onSuccess: (canary, variables) => {
      onCreated({
        id: canary.id,
        token: canary.token,
        name: canary.name,
        domain: variables.domain,
        domainId: canary.authorizedDomains[0]?.id ?? '',
      })
    },
    onError: (err) => {
      setError({ field: 'general', message: mapCreateError(err) })
    },
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    const trimmedName = name.trim()
    const normalizedDomain = normalizeDomain(domain)
    const validationError = validateInputs(trimmedName, normalizedDomain)
    if (validationError) {
      setError(validationError)
      return
    }
    mutation.mutate({ name: trimmedName, domain: normalizedDomain })
  }

  const nameError = error?.field === 'name' ? error.message : undefined
  const domainError = error?.field === 'domain' ? error.message : undefined

  return (
    <form
      onSubmit={handleSubmit}
      role="form"
      aria-label="Create your first canary"
      className="max-w-xl mx-auto space-y-8 py-8"
    >
      <div className="space-y-2">
        <h2
          ref={headingRef}
          tabIndex={-1}
          className="text-3xl font-bold text-gray-900 focus:outline-none"
        >
          Protect your first page
        </h2>
        <p className="text-gray-600">
          Tell us which page you want to protect. We&apos;ll create an invisible
          Canary you can embed on the page.
        </p>
      </div>

      <Input
        ref={nameRef}
        name="canary-name"
        label="What page do you want to protect?"
        placeholder="Donation Page"
        value={name}
        onChange={(e) => { setName(e.target.value); }}
        error={nameError}
        className="w-full"
      />

      <div>
        <Input
          name="canary-domain"
          label="What's the website address?"
          placeholder="example.com"
          value={domain}
          onChange={(e) => { setDomain(e.target.value); }}
          onBlur={() => { setDomain(normalizeDomain(domain)); }}
          error={domainError}
          className="w-full"
        />
        {!domainError && (
          <p className="mt-1 text-sm text-gray-500">
            The domain where this page lives (don&apos;t include https://)
          </p>
        )}
      </div>

      {error && (
        <div role="alert" className="bg-danger-50 text-danger-700 p-4 rounded-lg text-sm">
          {error.message}
        </div>
      )}

      <div className="flex justify-end">
        <Button
          type="submit"
          size="lg"
          isLoading={mutation.isPending}
          className="w-full sm:w-auto min-h-[44px]"
        >
          Create Canary
        </Button>
      </div>
    </form>
  )
}
